

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { backendApi } from '@/lib/api/backend'
import type { CamelCaseKeys } from '@/lib/utils/case-converter'
import type { components } from '@/types/generated/workspaces'

// Use CID-generated Workspace type, converted to camelCase to match interceptor output
export type Workspace = CamelCaseKeys<components['schemas']['Workspace']>

interface WorkspacesStore {
  workspaces: Workspace[]
  currentWorkspace: Workspace | null
  isLoading: boolean
  error: string | null

  // Actions
  setWorkspaces: (workspaces: Workspace[]) => void
  switchWorkspace: (workspaceId: string) => void
  setCurrentWorkspace: (workspace: Workspace | null) => void
  clearCurrentWorkspace: () => void
  refetchWorkspaces: (orgId: string) => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const useWorkspaces = create<WorkspacesStore>()(
  persist(
    (set, get) => ({
      workspaces: [],
      currentWorkspace: null,
      isLoading: false,
      error: null,

      setWorkspaces: (workspaces) => set({ workspaces }),

      switchWorkspace: (workspaceId) => {
        const workspace = get().workspaces.find((w) => w.id === workspaceId)
        if (workspace) {
          set({ currentWorkspace: workspace })
          // Store last used workspace
          if (typeof window !== 'undefined') {
            localStorage.setItem('lastWorkspaceId', workspaceId)
          }
        }
      },

      setCurrentWorkspace: (workspace) => set({ currentWorkspace: workspace }),

      clearCurrentWorkspace: () => set({ currentWorkspace: null }),

      refetchWorkspaces: async (orgId: string) => {
        set({ isLoading: true, error: null })
        try {
          const workspaces = await backendApi.workspaces.list({ organizationId: orgId })
          set({ workspaces, isLoading: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch workspaces',
            isLoading: false
          })
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),
    }),
    {
      name: 'workspaces-storage',
      partialize: (state) => ({
        currentWorkspace: state.currentWorkspace,
        workspaces: state.workspaces,
      }),
    }
  )
)

